import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ThermostatPreset, ThermostatPresetType } from '@app/modules/smart-devices/data/smart-devices.interface';

const UNIT_MAP = {
  s: 'seconds',
  m: 'minutes',
  h: 'hours',
  d: 'days',
};

@Component({
  selector: 'sbnb-preset-type',
  templateUrl: './preset-type.component.html',
  styles: `
    @import 'variables';
    .mode-button {
      padding: 4px 8px;
      font-size: 12px;
      border: 1px solid $smoke;
      background-color: #fff;
      cursor: pointer;
      border-radius: 4px;
      margin-right: 8px;
    }

    .active-mode {
      background-color: $grass-darker;
      color: #fff;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresetTypeComponent implements OnChanges, OnInit {
  @Input() preset: ThermostatPreset;
  @Input() type: { label: string; value: ThermostatPresetType; description: string };
  @Input() selected: boolean;
  @Output() update = new EventEmitter<ThermostatPreset>();

  public destroyRef = inject(DestroyRef);

  public description: { beforeTiming: string; afterTiming: string; hasTiming: boolean };
  public units = [
    {
      label: 'immediately',
      value: null,
    },
    {
      label: 'minutes',
      value: 'm',
    },
    {
      label: 'hours',
      value: 'h',
    },
    {
      label: 'days',
      value: 'd',
    },
  ];

  public timingUnitCtrl = new FormControl('h' as ThermostatPreset['timing']['unit']['value_append']);
  public timingValueCtrl = new FormControl(2);

  ngOnInit() {
    this.timingUnitCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value === null) {
        this.timingValueCtrl.setValue(0);
        return;
      }
      if (value === 'm') {
        this.timingValueCtrl.setValue(30);
        return;
      }
      if (value === 'h') {
        this.timingValueCtrl.setValue(2);
        return;
      }
      if (value === 'd') {
        this.timingValueCtrl.setValue(1);
        return;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['type']) {
      this.description = this.parseDescription(this.type.description);
      // only emitEvent == if the the change is the first time
      this.timingUnitCtrl.setValue(this.preset.timing.unit.value_append ?? null, { emitEvent: false });
      this.timingValueCtrl.setValue(this.preset.timing.unit.value, { emitEvent: false });
    }
  }

  parseDescription(description: string): { beforeTiming: string; afterTiming: string; hasTiming: boolean } {
    const timingIndex = description.indexOf('%timing%');
    if (timingIndex === -1) {
      return {
        beforeTiming: description,
        afterTiming: '',
        hasTiming: false,
      };
    }

    return {
      beforeTiming: description.substring(0, timingIndex),
      afterTiming: description.substring(timingIndex + 8),
      hasTiming: true,
    };
  }

  updatePreset(preset: ThermostatPreset, type: ThermostatPresetType) {
    const updatedPreset = {
      ...preset,
      type,
      time: `${this.timingValueCtrl.value ?? 0}${this.timingUnitCtrl.value}`,
      timing: {
        unit: {
          value: this.timingValueCtrl.value ?? 0,
          value_append: this.timingUnitCtrl.value,
        },
        when: 'before',
      },
    };

    this.update.emit(updatedPreset);
  }

  public get timingButtonLabel(): string {
    if (!this.timingValueCtrl.value) {
      return 'immediately';
    }
    const unit =
      this.timingValueCtrl.value === 1
        ? UNIT_MAP[this.timingUnitCtrl.value].slice(0, -1)
        : UNIT_MAP[this.timingUnitCtrl.value];
    return `${this.timingValueCtrl.value ?? 0} ${unit}`;
  }
}
